import { UseModal } from "@/components/root/modal";
import { ActivityDirection, AiFileActivityId, InfoCategoryId } from "./model";
import {
  GenAiDrillScope,
  GenAiDrillWidget,
  set_gen_ai_drill_scope,
} from "./drill-scope";
import { AxisType, CoordinateInputTier3, TimeAxisType } from "@/gql/types";
import { EventsModalProps } from "@/components/modals/events-modal/model";
// the gen-ai copy, so "Activity Type" / "AI Agent" naming stays off every
// other source that shares the original modal
import EventsModalVue from "../modals/GenAiEventsModal.vue";
import { gen_ai_mock_enabled } from "./mock-mode";
import { gen_ai_scope_cord } from "./state";
import { SharedState } from "@/common/state";

/**
 * Live, the list is asked for exactly what the card counted: the AI relation
 * codes and the category's L0 label, as one more coordinate (see
 * `gen_ai_scope_cord`). Without it the list held every event in the month.
 * In mock mode there is none, and the drill scope feeds the demo rows.
 */
const scope_cords = (
  scope: Parameters<typeof gen_ai_scope_cord>[0] & { actor_id?: string }
) => {
  const frame = SharedState.calculatedTimeFrame.value.frame;
  const cord = gen_ai_scope_cord(scope, frame);
  if (!cord) return [];
  // one employee's interactions (Widget 5): the person, ANDed with the rest
  const person = scope.actor_id
    ? [
        {
          x: { type: AxisType.Employee, values: [scope.actor_id] },
          y: { type: AxisType.None, values: [] },
          z: frame,
        } as CoordinateInputTier3,
      ]
    : [];
  return [cord, ...person];
};

/**
 * Mock-plan activation for the Gen AI source.
 *
 * The plan's `activate` flag is global, so it is turned on while the Gen AI
 * overview is mounted and restored when leaving it.
 *
 * It deliberately is NOT scoped to the modal: EventsModal implements the
 * Content/Events switch by emitting "swap" and opening FilesModal itself, which
 * resolves the `UseModal().show()` promise. Restoring the flag there would
 * disable the mocks the moment the user switches tabs (rows then vanish).
 */
let was_active = false;
let is_enabled = false;

export const enable_gen_ai_mocks = () => {
  // In live mode this must do nothing: flipping the plan on would override
  // the env's own `mock: false` and serve mocked rows to the drill-downs while
  // the cards behind them read the real API.
  if (!gen_ai_mock_enabled()) return;

  // @ts-ignore — created at boot for local/debug/qa/uat/preview envs
  const plan = window._gql_mock_plan;
  // the guard keeps a remount from capturing our own `true` as the original
  if (!plan || is_enabled) {
    return;
  }
  was_active = plan.activate;
  plan.activate = true;
  is_enabled = true;
};

export const restore_gen_ai_mocks = () => {
  // @ts-ignore
  const plan = window._gql_mock_plan;
  if (!plan || !is_enabled) {
    return;
  }
  plan.activate = was_active;
  is_enabled = false;
};

/**
 * Opens the app's real events/content modal, fed by `src/mock/api/gen-ai.mock.ts`.
 *
 * The scope is always set, including to null, so a previous drill-down's
 * relation/category can never leak into this list. Callers that know what
 * produced the number pass it: the list is then filtered by that, not merely
 * titled after it — which is what the matrix and "information accessed" cards
 * were doing before, naming a category in the heading while listing every
 * event in the source.
 */
export const show_gen_ai_events = (
  title: string,
  scope: GenAiDrillScope | null = null,
  opts: { ai_activities?: boolean } = {}
) => {
  set_gen_ai_drill_scope(scope);
  UseModal().show(EventsModalVue, {
    title,
    files_title: title.replace("Events", "Content"),
    // an AI activities list has no content side — it is a log of what the
    // agent did, not a set of files — so the Content/Events switch goes
    events_only: !!opts.ai_activities,
    ai_activities: !!opts.ai_activities,
    // Always locked on this dashboard. Every list here is opened from a widget
    // that already decided what it is about, so the category is reported, not
    // chosen. Defaulted inside the drill-down rather than passed per call site,
    // because there are seven of them and passing it at each is how sites get
    // missed.
    lock_info_types: true,
    // Applications says nothing on this dashboard: every row is a Copilot
    // interaction, so the filter only ever has one value to offer.
    hide_filters: ["sources"],
    show_back: true,
    coordinates: [
      {
        x: { type: AxisType.None, values: [] },
        y: { type: AxisType.None, values: [] },
        z: { type: TimeAxisType.Month, values: [] },
      },
      ...scope_cords(scope || {}),
    ] as CoordinateInputTier3[],
  } as EventsModalProps);
};

/**
 * The risk events behind one "Risk By Information Category" row. Ami: the rows
 * open the event list filtered by the category and severity != "none".
 *
 * Two coordinates. EventsModal seeds its visible filters from coordinates[0]
 * only (and only from values[0]), so the first coordinate stays empty and the
 * second carries Severity ["1","2","3"] — the app's standing encoding for "any
 * real severity" (detection/data/api.ts).
 *
 * The severity filter stays VISIBLE. There is no "None" option in it to begin
 * with: `UseFilters` builds severities from `items: [1, 2, 3]`, so severity !=
 * none is what that control already means. Hiding it only removed the user's
 * ability to narrow. The pin still does real work — `merge_and_fix_cords`
 * spreads filter-derived axes over passed ones (`{...before, ...other}`), so
 * ["1","2","3"] applies until the user picks a narrower set, which then wins.
 *
 * The category travels as its L0 label id, never the gen-ai id ("personal",
 * "hr", ...): the original modal's filter seeding called getInfoType() on a
 * gen-ai id and crashed on the miss (undefined .labelType), blanking the list.
 */
export const show_gen_ai_category_events = (
  item: { name: string },
  opts: {
    severities?: number[];
    level_text?: string;
    /** the card's expand — every category rather than the clicked one */
    all_categories?: boolean;
    /**
     * The AI relation behind the number, e.g. "Read by AI".
     *
     * Named in the heading AND applied to the list: live, as the relation's
     * codes on the AiContentRelation axis; in mock mode, out-of-band through
     * drill-scope.ts, which the gen-ai mock reads back when serving rows.
     */
    relation?: { id: AiFileActivityId; name: string };
    /** employee -> AI or AI -> content; same out-of-band route */
    direction?: ActivityDirection;
    /** the gen-ai category id ("personal", ...) — same out-of-band route */
    category_id?: InfoCategoryId;
    /** which card was clicked; selects the event set the mock reads */
    widget?: GenAiDrillWidget;
    /** the AI activities variant (widgets 1 and 5) */
    ai_activities?: boolean;
  } = {}
) => {
  // default to every real severity; the card passes whatever its slider shows
  const severities = (opts.severities && opts.severities.length
    ? opts.severities
    : [1, 2, 3]
  )
    .slice()
    .sort()
    .map(String);

  set_gen_ai_drill_scope({
    widget: opts.widget,
    relation: opts.relation && opts.relation.id,
    direction: opts.direction,
    category: opts.all_categories ? undefined : opts.category_id,
  });

  // gen-ai-ui's list titles (metadata/forms.json `events`): "{category}: Risky
  // Shares to AI" for the employees' card, "{activity}: Risky AI Agent
  // Activities" for the agents' one. The direction is always named, so the
  // list is never mistaken for the other card's — the live events query cannot
  // filter on it yet (see drill-scope.ts). A category chip that narrowed the
  // number is named too, since nothing else in the list shows it.
  const in_category = opts.all_categories ? "" : ` · <b>${item.name}</b>`;
  const title =
    opts.direction === "employee"
      ? `<b>${item.name}</b>: Risky Shares to AI`
      : opts.direction === "ai"
      ? (opts.relation
          ? `<b>${opts.relation.name}</b>: Risky AI Agent Activities`
          : "Risky AI Agent Activities") + in_category
      : opts.relation
      ? `<b>${opts.relation.name}</b>: Risk Events${in_category}`
      : `Risk Events${in_category}`;
  const files_title = opts.all_categories
    ? "Files included in Risk Events"
    : `<b>${item.name}</b> files included in Risk Events`;

  UseModal().show(EventsModalVue, {
    title,
    files_title,
    // Applications says nothing on this dashboard: every row is a Copilot
    // interaction, so the filter only ever has one value to offer.
    hide_filters: ["sources"],
    events_only: !!opts.ai_activities,
    ai_activities: !!opts.ai_activities,
    lock_info_types: true,
    show_back: true,
    coordinates: [
      {
        x: { type: AxisType.None, values: [] },
        y: { type: AxisType.None, values: [] },
        z: { type: TimeAxisType.Month, values: [] },
      },
      {
        x: { type: AxisType.None, values: [] },
        y: { type: AxisType.Severity, values: severities },
        z: { type: TimeAxisType.Month, values: [] },
      },
      ...scope_cords({
        relation: opts.relation && opts.relation.id,
        direction: opts.direction,
        category: opts.all_categories ? undefined : opts.category_id,
      }),
    ] as CoordinateInputTier3[],
  } as EventsModalProps);
};

/**
 * Expanded "What is your information?"-style breakdown. Ami: the expand of
 * "Information involved in AI activity" and of "# Shared By Employees" are the
 * same view — same query as the widget being expanded, just a deeper label.
 */
export const show_gen_ai_breakdown = async (
  title: string,
  items: unknown[]
) => {
  const GenAiBreakdownModal = (
    await import("../modals/GenAiBreakdownModal.vue")
  ).default;
  UseModal().show(GenAiBreakdownModal, {
    title,
    items,
    frame_date: SharedState.selectedTimeFrameDisplayName.value,
  });
};

/**
 * The employees behind a Widget 5 row, at the sensitivity levels its slider
 * includes — the same virtual labels the row counted.
 */
export const show_gen_ai_employees = async (
  title: string,
  category: string | null,
  levels: readonly string[] = ["high", "medium", "low", "none"]
) => {
  const GenAiEmployeesModal = (
    await import("../modals/GenAiEmployeesModal.vue")
  ).default;
  UseModal().show(GenAiEmployeesModal, { title, category, levels: [...levels] });
};

/*
 * The two AI-agent drill-downs (`show_gen_ai_matrix`, `show_gen_ai_agents`)
 * lived here with their cards and modals. Nothing on the dashboard reached
 * them, so they were removed along with AiAgentsByActivityCard,
 * AiAgentsMatrixCard, AiAgentsListModal and AiAgentsMatrixModal.
 *
 * Their data is still in place — `agents_all`, `agents_by_activity` and
 * `agents_matrix` on GenAiOverview, plus the AiAgent / AiAgentMatrixRow models.
 * To bring the UI back when the agents view lands, restore just those four
 * files from c17c644b (the last commit that still contains them) — naming the
 * paths matters, as checking out the whole directory would revert everything
 * else in here too:
 *
 *   git checkout c17c644b -- \
 *     src/views/overview/sources/ms-365-gen-ai/card-items/AiAgentsByActivityCard.vue \
 *     src/views/overview/sources/ms-365-gen-ai/card-items/AiAgentsMatrixCard.vue \
 *     src/views/overview/sources/ms-365-gen-ai/modals/AiAgentsListModal.vue \
 *     src/views/overview/sources/ms-365-gen-ai/modals/AiAgentsMatrixModal.vue
 */
